import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { z } from "zod";

export const QUIZ_GENERATION_STATUS_POLL_INTERVAL = 1000;

// eslint-disable-next-line no-restricted-syntax
export enum QuizGenerationResponseLogStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  SUCCESSFUL = "SUCCESSFUL",
  REJECTED = "REJECTED",
}

// eslint-disable-next-line no-restricted-syntax
export enum QuizGenerationRequestRejectionReason {
  LESS_THAN_REQUIRED_NOTES = "LESS_THAN_REQUIRED_NOTES",
  MORE_THAN_ALLOWED_NOTES = "MORE_THAN_ALLOWED_NOTES",
}

export const quizGenerationResponseLogSchema = z.object({
  type: z.literal("workplace-quiz-generation-request"),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(QuizGenerationResponseLogStatus),
    requestedAt: z.string(),
    rejectionReasons: z.array(z.nativeEnum(QuizGenerationRequestRejectionReason)).optional(),
  }),
});

const usePollQuizGenerationStatusResponseSchema = z.object({
  data: z.array(quizGenerationResponseLogSchema),
});

export type UsePollQuizGenerationStatusResponse = z.infer<
  typeof usePollQuizGenerationStatusResponseSchema
>;

export type QuizGenerationStatusResponseData = UsePollQuizGenerationStatusResponse["data"];

export function getQuizGenerationStatusUrl(facilityId: string): string {
  return `${environmentConfig.REACT_APP_API_MAIN_URL}/api/workplaces/${facilityId}/quiz-generation-requests`;
}

export function usePollQuizGenerationStatus(
  facilityId: string,
  options: UseGetQueryOptions<
    UsePollQuizGenerationStatusResponse,
    QuizGenerationStatusResponseData
  > = {}
) {
  return useGetQuery({
    // Get last 2 quiz generation requests
    url: `${getQuizGenerationStatusUrl(facilityId)}?sort=-requestedAt&page[size]=2`,
    responseSchema: usePollQuizGenerationStatusResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_QUIZ_GENERATION_STATUS_FAILURE,
    },
    select: (data) => data.data,
    refetchInterval: QUIZ_GENERATION_STATUS_POLL_INTERVAL,
    ...options,
  });
}
